// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Modal, TextInput, TextArea, ToggleSwitch, StatusBanner } from '@loa/ui-components';
import { TAG_PALETTE_TOKENS } from '../../types/tag';
import type { TagDTO } from '../../types/tag';
import './TagFormModal.css';

export interface TagFormValues {
  name: string;
  color: string;
  description: string;
  archived: boolean;
}

export interface TagFormModalProps {
  open: boolean;
  /** Present when editing an existing tag; omit for create. */
  tag?: TagDTO | null;
  onClose: () => void;
  onSubmit: (values: TagFormValues) => Promise<void>;
}

const swatchHex: Record<string, string> = {
  slate: '#E2E8F0', red: '#FEE2E2', orange: '#FFEDD5', amber: '#FEF3C7',
  emerald: '#D1FAE5', teal: '#CCFBF1', sky: '#E0F2FE', blue: '#DBEAFE',
  indigo: '#E0E7FF', violet: '#EDE9FE', fuchsia: '#FAE8FF', rose: '#FFE4E6',
};

/**
 * Create/edit modal for a tag definition (Tags PRD §9 "create/edit modal
 * (name, palette swatch picker, description, archive toggle)"). The archive
 * toggle only renders when editing (create can't start archived).
 */
export function TagFormModal({ open, tag, onClose, onSubmit }: Readonly<TagFormModalProps>) {
  const isEdit = !!tag;
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(TAG_PALETTE_TOKENS[0]);
  const [description, setDescription] = useState('');
  const [archived, setArchived] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(tag?.name ?? '');
    setColor(tag?.color ?? TAG_PALETTE_TOKENS[0]);
    setDescription(tag?.description ?? '');
    setArchived(tag?.archived ?? false);
    setError(null);
  }, [open, tag]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit({ name: trimmed, color, description: description.trim(), archived });
      onClose();
    } catch (err) {
      const apiError = err as { response?: { status?: number; data?: { message?: string } } };
      if (apiError.response?.status === 409) {
        setError('A tag with this name already exists.');
      } else {
        setError(apiError.response?.data?.message || 'Failed to save tag. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} size="small">
      <Modal.Header icon={isEdit ? 'edit' : 'plus'} title={isEdit ? 'Edit tag' : 'Create tag'} onClose={handleClose} />
      <Modal.Content>
        {error && (
          <StatusBanner variant="error" message={error} visible position="inline" dismissible={false} />
        )}
        <div className="flex flex-col gap-[20px]">
          <TextInput
            label="Name"
            placeholder="e.g. high-net-worth"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={50}
            autoFocus
            required
            fluid
          />

          <div>
            <div className="tag-form-modal__swatch-label">Colour</div>
            <div className="tag-form-modal__swatches" role="radiogroup" aria-label="Tag colour">
              {TAG_PALETTE_TOKENS.map((token) => (
                <button
                  key={token}
                  type="button"
                  role="radio"
                  aria-checked={color === token}
                  aria-label={token}
                  className={`tag-form-modal__swatch ${color === token ? 'tag-form-modal__swatch--selected' : ''}`}
                  style={{ background: swatchHex[token] }}
                  onClick={() => setColor(token)}
                />
              ))}
            </div>
          </div>

          <TextArea
            label="Description"
            placeholder="Optional, up to 200 characters"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={200}
            fluid
          />

          {isEdit && (
            <div className="flex items-center justify-between">
              <span className="tag-form-modal__archive-label">Archived</span>
              <ToggleSwitch checked={archived} onChange={setArchived} aria-label="Archive tag" />
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions
        secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: saving }}
        primaryAction={{
          label: isEdit ? 'Save changes' : 'Create tag',
          onClick: () => { void handleSubmit(); },
          loading: saving,
          disabled: saving,
        }}
      />
    </Modal>
  );
}
